import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AnimatePresence } from 'framer-motion'
import { useAuth } from '@/contexts/AuthContext'
import { useToast } from '@/hooks/use-toast'
import { supabase } from '@/lib/supabase'
import { isAdmin, isManager, isSuperAdmin } from '@/utils/permissions'
import { seedOnboardingItems } from '@/services/onboardingItemsService'
import { OnboardingProgress } from './OnboardingProgress'
import { OnboardingStepWelcome } from './OnboardingStepWelcome'
import { OnboardingStepProject } from './OnboardingStepProject'
import { OnboardingStepSetup } from './OnboardingStepSetup'
import { OnboardingStepReady } from './OnboardingStepReady'
import type { OnboardingProjectData, OnboardingPath } from './OnboardingStepProject'
import type { Project } from '@/types'

interface WelcomeOnboardingProps {
  onComplete: () => void
  onSkip: () => void
  completeStep: (step: number) => Promise<void>
  completeOnboarding: () => Promise<void>
  initialStep?: number
  resumeProject?: Project
}

export function WelcomeOnboarding({ onComplete, onSkip, completeStep, completeOnboarding, initialStep, resumeProject }: WelcomeOnboardingProps) {
  const { user } = useAuth()
  const { toast } = useToast()
  const navigate = useNavigate()
  const [step, setStep] = useState(initialStep ?? 1)
  const [projectId, setProjectId] = useState<string | null>(resumeProject?.id ?? null)
  const [projectPath, setProjectPath] = useState<OnboardingPath>(() => {
    if (!resumeProject) return 'build_new'
    return resumeProject.project_type === 'manage_only' ? 'existing' : 'build_new'
  })
  const [projectPlatform, setProjectPlatform] = useState(() => {
    if (!resumeProject) return 'other'
    if (resumeProject.source_url?.includes('shopify')) return 'shopify'
    if (resumeProject.data?.phase1?.project_category === 'ecommerce') return 'shopify'
    return resumeProject.data?.phase1?.project_category ?? 'other'
  })
  const [projectLiveUrl, setProjectLiveUrl] = useState(() => {
    if (!resumeProject) return ''
    return resumeProject.source_url || resumeProject.data?.phase5?.live_url || ''
  })
  const [isCreating, setIsCreating] = useState(false)

  const goToStep = async (nextStep: number) => {
    await completeStep(step)
    setStep(nextStep)
  }

  const handleCreateProject = async (data: OnboardingProjectData) => {
    if (!user) return
    setIsCreating(true)
    try {
      const agencyId = user.agency_id
      if (!agencyId) {
        toast({ variant: 'destructive', title: 'Error', description: 'No account found. Please refresh and try again.' })
        return
      }

      const phase1 = {
        business_name: data.businessName,
        project_category: data.projectCategory,
        industry: data.industry,
        goal: '',
        audience: data.targetAudience,
        primary_device: 'both' as const,
        is_ecommerce: data.projectCategory === 'ecommerce',
        pages: [],
        custom_pages: [],
        page_names: {},
        page_content: {},
        legal_content_preference: 'not_sure' as const,
        has_logo: false,
        logo_url: null,
        colors: { primary: '', secondary: '', accent: '', no_idea: true },
        font: { primary: '', secondary: '', custom_fonts: [] },
        style_keywords: [],
        references: [],
        ai_conversation: {
          project_idea: data.projectDescription,
          follow_up_questions: [],
          answers: {},
        },
      }

      const isBuildNew = data.path === 'build_new'

      const projectInsert = isBuildNew
        ? {
            name: data.businessName,
            user_id: user.id,
            agency_id: agencyId,
            created_by: user.id,
            status: 'draft',
            project_type: 'standard',
            source_url: data.liveUrl || null,
            current_step: 1,
            onboarding_status: 'dashboard_onboarding',
            data: {
              phase1,
              phase2: { pages: [] },
              phase3: {},
              phase4: { demo_url: '', pages: [] },
              phase5: { items: [], cycles: [] },
            },
          }
        : {
            name: data.businessName,
            user_id: user.id,
            agency_id: agencyId,
            created_by: user.id,
            status: 'in_management',
            project_type: 'manage_only',
            source_url: data.liveUrl || null,
            current_step: 5,
            onboarding_status: 'dashboard_onboarding',
            enabled_modules: ['manage'],
            visible_modules: ['manage'],
            data: {
              phase1,
              phase2: { pages: [] },
              phase3: {},
              phase4: { demo_url: '', pages: [] },
              phase5: { live_url: data.liveUrl || undefined, items: [], cycles: [] },
            },
          }

      const { data: project, error } = await supabase
        .from('projects')
        .insert(projectInsert)
        .select()
        .single()

      if (error) throw error

      // Add user as project member
      if (!isSuperAdmin(user)) {
        const memberRole: 'manager' | 'client' = (isAdmin(user) || isManager(user)) ? 'manager' : 'client'
        await supabase.from('project_members').insert({
          project_id: project.id,
          user_id: user.id,
          role: memberRole,
        })
      }

      // Seed onboarding inbox messages
      await seedOnboardingItems(project.id, agencyId, user.id)

      setProjectId(project.id)
      setProjectPath(data.path)
      setProjectPlatform(data.platform)
      setProjectLiveUrl(data.liveUrl)
      await goToStep(3)
    } catch (err) {
      console.error('Error creating project:', err)
      toast({ variant: 'destructive', title: 'Error', description: 'Failed to create project. Please try again.' })
    } finally {
      setIsCreating(false)
    }
  }

  const handleFinish = async () => {
    // Mark project onboarding as complete before navigating
    if (projectId) {
      await supabase
        .from('projects')
        .update({ onboarding_status: 'complete' })
        .eq('id', projectId)
    }
    await completeOnboarding()
    if (projectId) {
      navigate(`/project/${projectId}`)
    } else {
      onComplete()
    }
  }

  const handleSkipToReady = async () => {
    // Mark project complete if one was already created (e.g. during resume)
    if (projectId) {
      await supabase
        .from('projects')
        .update({ onboarding_status: 'complete' })
        .eq('id', projectId)
    }
    await goToStep(4)
  }

  return (
    <div className="py-12 px-4">
      <div className="max-w-2xl mx-auto mb-10">
        <OnboardingProgress currentStep={step} totalSteps={4} />
      </div>

      <AnimatePresence mode="wait">
        {step === 1 && (
          <OnboardingStepWelcome
            onContinue={() => goToStep(2)}
            onSkip={onSkip}
          />
        )}
        {step === 2 && (
          <OnboardingStepProject
            onContinue={handleCreateProject}
            onBack={() => setStep(1)}
            onSkip={handleSkipToReady}
            isCreating={isCreating}
          />
        )}
        {step === 3 && projectId && (
          <OnboardingStepSetup
            projectId={projectId}
            projectPath={projectPath}
            projectPlatform={projectPlatform}
            liveUrl={projectLiveUrl}
            onContinue={() => goToStep(4)}
            onBack={() => setStep(2)}
          />
        )}
        {step === 4 && (
          <OnboardingStepReady
            onFinish={handleFinish}
            hasProject={!!projectId}
          />
        )}
      </AnimatePresence>
    </div>
  )
}
